/**
 * PlatformConnectionGate
 * Blocks access to targeting configuration if user hasn't connected their ad account
 * Shows a prominent CTA to connect the platform
 */

import React from 'react';
import { Link2, AlertTriangle, ExternalLink } from 'lucide-react';

const platformInfo = {
    meta: {
        name: 'Meta (Facebook/Instagram)',
        icon: '📘',
        description: 'Connect your Meta Business account to access targeting options like interests, behaviors, and custom audiences.',
        connectPath: '/settings/connections'
    },
    google: {
        name: 'Google Ads',
        icon: '🔍',
        description: 'Connect your Google Ads account to access audience segments, keywords, and remarketing lists.',
        connectPath: '/settings/connections'
    },
    tiktok: {
        name: 'TikTok Ads',
        icon: '🎵',
        description: 'Connect your TikTok Business Center to access interest categories, behaviors, and custom audiences.',
        connectPath: '/settings/connections'
    },
    linkedin: {
        name: 'LinkedIn Ads',
        icon: '💼',
        description: 'Connect your LinkedIn Campaign Manager to access professional targeting like job titles, industries, and company sizes.',
        connectPath: '/settings/connections'
    }
};

export default function PlatformConnectionGate({
    platform,
    isConnected,
    onConnect,
    children,
    accountName = null
}) {
    const info = platformInfo[platform] || platformInfo.meta;

    if (isConnected) {
        return (
            <div className="platform-connection-status">
                {accountName && (
                    <div className="connected-account-badge">
                        <span className="platform-icon">{info.icon}</span>
                        <span className="account-name">{accountName}</span>
                        <span className="connected-indicator">Connected</span>
                    </div>
                )}
                {children}
            </div>
        );
    }

    return (
        <div className="platform-connection-gate">
            <div className="gate-content">
                <div className="gate-icon">
                    <AlertTriangle size={48} className="warning-icon" />
                </div>

                <h3 className="gate-title">
                    {info.icon} Connect {info.name}
                </h3>

                <p className="gate-description">
                    {info.description}
                </p>

                <div className="gate-benefits">
                    <h4>With a connected account, you can:</h4>
                    <ul>
                        <li>Search and select valid targeting options with IDs</li>
                        <li>See real-time audience size estimates</li>
                        <li>Access your custom and lookalike audiences</li>
                        <li>Export campaigns directly to the platform</li>
                    </ul>
                </div>

                <div className="gate-actions">
                    <button
                        className="connect-button primary"
                        onClick={onConnect}
                    >
                        <Link2 size={18} />
                        Connect {info.name}
                    </button>

                    <a
                        href={info.connectPath}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="settings-link"
                    >
                        <ExternalLink size={14} />
                        Go to Connection Settings
                    </a>
                </div>
            </div>

            <style>{`
                .platform-connection-gate {
                    padding: 2rem;
                    background: var(--color-surface);
                    border: 2px dashed var(--color-border);
                    border-radius: 12px;
                    text-align: center;
                }

                .gate-content {
                    max-width: 500px;
                    margin: 0 auto;
                }

                .gate-icon {
                    margin-bottom: 1rem;
                }

                .warning-icon {
                    color: var(--color-warning);
                }

                .gate-title {
                    font-size: 1.25rem;
                    font-weight: 600;
                    margin-bottom: 0.75rem;
                    color: var(--color-text-primary);
                }

                .gate-description {
                    color: var(--color-text-tertiary);
                    margin-bottom: 1.5rem;
                    line-height: 1.5;
                }

                .gate-benefits {
                    text-align: left;
                    background: var(--color-bg-primary);
                    padding: 1rem;
                    border-radius: 8px;
                    margin-bottom: 1.5rem;
                }

                .gate-benefits h4 {
                    font-size: 0.875rem;
                    font-weight: 600;
                    margin-bottom: 0.5rem;
                    color: var(--color-text-primary);
                }

                .gate-benefits ul {
                    margin: 0;
                    padding-left: 1.25rem;
                }

                .gate-benefits li {
                    font-size: 0.875rem;
                    color: var(--color-text-tertiary);
                    margin-bottom: 0.25rem;
                }

                .gate-actions {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    gap: 0.75rem;
                }

                .connect-button {
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                    padding: 0.75rem 1.5rem;
                    background: var(--color-primary);
                    color: var(--color-text-inverted);
                    border: none;
                    border-radius: 8px;
                    font-weight: 500;
                    cursor: pointer;
                    transition: opacity 0.2s;
                }

                .connect-button:hover {
                    opacity: 0.9;
                }

                .settings-link {
                    display: flex;
                    align-items: center;
                    gap: 0.25rem;
                    font-size: 0.875rem;
                    color: var(--color-text-tertiary);
                    text-decoration: none;
                }

                .settings-link:hover {
                    color: var(--color-text-primary);
                }

                .connected-account-badge {
                    display: inline-flex;
                    align-items: center;
                    gap: 0.5rem;
                    padding: 0.5rem 1rem;
                    background: var(--color-bg-primary);
                    border: 1px solid var(--color-border);
                    border-radius: 20px;
                    margin-bottom: 1rem;
                    font-size: 0.875rem;
                }

                .platform-icon {
                    font-size: 1rem;
                }

                .account-name {
                    color: var(--color-text-primary);
                    font-weight: 500;
                }

                .connected-indicator {
                    color: var(--color-success);
                    font-size: 0.75rem;
                }
            `}</style>
        </div>
    );
}
